import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { useErrorToast } from '@/composables/useErrorToast'

import Network from '@/Network'
import type {
  ImportQualificationValidationsParams,
  QualificationPolicyType
} from '@/Network/Types/Requests/QualificationPolicyRequests'
import type { ImportValidationsResponse } from '@/Network/Types/Responses/QualificationPolicyResponses'

type PolicyType = QualificationPolicyType

const DEFAULT_POLICY_TYPE: PolicyType = 'TRANSFER_QUALIFICATION'

export const useImportQualificationValidationsStore = defineStore(
  'importQualificationValidations',
  () => {
    // Legado tolerado (rule error-handling): o toast fica na store porque os callers
    // tratam só o boolean de retorno; a mensagem passa pelo funil do `notifyError`.
    const { notifyError, notifyErrorMessage } = useErrorToast()

    const scheduleConflicts = ref<Record<number, string>>({})
    const isValidatingSchedules = ref(false)
    const isImporting = ref(false)
    const uploadProgress = ref(0)
    const uploadLoaded = ref(0)
    const uploadTotal = ref(0)
    const importResult = ref<ImportValidationsResponse | null>(null)

    let importAbortController: AbortController | null = null

    async function fetchScheduleConflicts(
      companyId: number,
      scheduleIds: number[],
      policyType: PolicyType = DEFAULT_POLICY_TYPE
    ) {
      if (!scheduleIds.length) {
        scheduleConflicts.value = {}
        return
      }
      try {
        const service =
          policyType === 'DOCUMENTARY_QUALIFICATION'
            ? Network.qualificationImport.qualificationDocumentalPolicy
            : Network.qualificationImport.qualificationTransferPolicy
        const result = await service.validateImportSchedules(companyId, scheduleIds)
        scheduleConflicts.value = Object.fromEntries(
          result.conflicts.map((c) => [c.scheduleId, c.policyName])
        )
      } catch (e) {
        console.error(e)
        scheduleConflicts.value = {}
        throw e
      }
    }

    async function validateSchedules(
      companyId: number,
      scheduleIds: number[],
      policyType: PolicyType = DEFAULT_POLICY_TYPE
    ): Promise<boolean> {
      isValidatingSchedules.value = true
      try {
        const service =
          policyType === 'DOCUMENTARY_QUALIFICATION'
            ? Network.qualificationImport.qualificationDocumentalPolicy
            : Network.qualificationImport.qualificationTransferPolicy
        const result = await service.validateImportSchedules(companyId, scheduleIds)
        if (!result.valid) {
          const label =
            policyType === 'DOCUMENTARY_QUALIFICATION'
              ? 'qualificação documental'
              : 'qualificação para repasse'
          notifyErrorMessage(
            `As escalas selecionadas já possuem uma política de ${label} vigente e ativa.`
          )
          return false
        }
        return true
      } catch (error: unknown) {
        console.error(error)
        notifyError(error, 'Erro ao validar escalas selecionadas.')
        return false
      } finally {
        isValidatingSchedules.value = false
      }
    }

    async function importValidations(
      params: ImportQualificationValidationsParams,
      policyType: PolicyType = DEFAULT_POLICY_TYPE
    ): Promise<boolean> {
      importAbortController?.abort()
      importAbortController = new AbortController()

      isImporting.value = true
      uploadProgress.value = 0
      uploadLoaded.value = 0
      uploadTotal.value = params.file.size

      try {
        const service =
          policyType === 'DOCUMENTARY_QUALIFICATION'
            ? Network.qualificationImport.qualificationDocumentalPolicy
            : Network.qualificationImport.qualificationTransferPolicy
        const response = await service.importValidations(
          params,
          (event) => {
            uploadLoaded.value = event.loaded
            uploadTotal.value = event.total ?? params.file.size
            uploadProgress.value = Math.min(
              99,
              Math.round((event.loaded * 100) / (event.total || 1))
            )
          },
          importAbortController.signal
        )

        importResult.value = response
        return true
      } catch (error: unknown) {
        if (axios.isCancel(error)) return false
        console.error(error)
        notifyError(error, 'Erro ao importar validações')
        return false
      } finally {
        isImporting.value = false
        importAbortController = null
      }
    }

    async function downloadTemplate(policyType: PolicyType = DEFAULT_POLICY_TYPE): Promise<void> {
      try {
        const service =
          policyType === 'DOCUMENTARY_QUALIFICATION'
            ? Network.qualificationImport.qualificationDocumentalPolicy
            : Network.qualificationImport.qualificationTransferPolicy
        const blob = await service.downloadImportTemplate(policyType)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `template-${policyType}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error: unknown) {
        console.error(error)
        notifyError(error, 'Erro ao baixar o modelo de importação.')
      }
    }

    function $reset() {
      importAbortController?.abort()
      importAbortController = null
      isValidatingSchedules.value = false
      isImporting.value = false
      uploadProgress.value = 0
      uploadLoaded.value = 0
      uploadTotal.value = 0
      importResult.value = null
      scheduleConflicts.value = {}
    }

    return {
      scheduleConflicts,
      isValidatingSchedules,
      isImporting,
      uploadProgress,
      uploadLoaded,
      uploadTotal,
      importResult,
      fetchScheduleConflicts,
      validateSchedules,
      importValidations,
      downloadTemplate,
      $reset
    }
  }
)
